import { useState } from 'react';

import { ApprovalChangeField } from './approval-change-field';
import { InlineApprovalActions } from './inline-approval-actions';
import { InlineApprovalCardShell } from './inline-approval-card-shell';
import type { PatientContextApprovalView } from './patient-context-approval';
import { DetailsSection } from './routine-proposal-details';

interface PatientContextApprovalCardProps extends PatientContextApprovalView {
  onApprove: () => void;
  onDeny: () => void;
  pendingVerdict: 'approve' | 'deny' | null;
  disabled?: boolean;
  /** Disable Approve until the current profile has loaded. Cancel stays available. */
  approveDisabled?: boolean;
  /** Profile fetch is in flight. Details stays open on a shimmer instead of the diff. */
  detailsPending?: boolean;
  /** Create has no previous value, so the Before column stays off. */
  showBefore?: boolean;
  'data-testid'?: string;
}

export const PatientContextApprovalCard = ({
  title,
  subtitle,
  diffs,
  onApprove,
  onDeny,
  pendingVerdict,
  disabled = false,
  approveDisabled = false,
  detailsPending = false,
  showBefore = true,
  'data-testid': dataTestId = 'patient-context-approval',
}: PatientContextApprovalCardProps) => {
  const [detailsOpen, setDetailsOpen] = useState(true);

  return (
    <InlineApprovalCardShell
      title={title}
      subtitle={subtitle}
      urgent={!disabled && pendingVerdict === null}
      data-testid={dataTestId}
    >
      {diffs.length > 0 || detailsPending ? (
        <DetailsSection
          open={detailsOpen}
          onToggle={() => setDetailsOpen((open) => !open)}
          placeholder={
            detailsPending ? (
              <div
                data-testid="patient-context-details-skeleton"
                className="relative h-12 w-full overflow-hidden rounded-lg bg-background-tertiary"
              >
                <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                  <div className="absolute inset-y-0 w-full animate-shimmer-parallelogram bg-surface/25 motion-reduce:animate-none" />
                </div>
              </div>
            ) : undefined
          }
        >
          <div className="flex flex-col gap-5">
            {diffs.map((diff) => (
              <ApprovalChangeField
                key={diff.key}
                label={diff.label}
                before={diff.before}
                after={diff.after}
                showBefore={showBefore}
              />
            ))}
          </div>
        </DetailsSection>
      ) : null}
      <InlineApprovalActions
        onApprove={onApprove}
        onDeny={onDeny}
        pendingVerdict={pendingVerdict}
        disabled={disabled}
        approveDisabled={approveDisabled}
      />
    </InlineApprovalCardShell>
  );
};
